import AggregateError from 'es-aggregate-error';
import { action, runInAction } from 'mobx';

import {
  type Attributes,
  Collection,
  type IBaseAttributes,
  isWretchError,
  Model,
  type TConstraints,
  wretch,
} from '@feathr/rachis';

import type { IAccount } from './accounts';
import type { IEvent as IProject } from './events';

export type TFundraiserState = 'connected' | 'failed' | 'not_connected' | 'pending';

export type TFundraiserCRM = 'SalesforceCRM' | 'BlackbaudCRM';
export interface IFundraiserCRMIntegration {
  /** Ephemeral flag indicating if the integration is ready to be enabled */
  ready_to_enable_integration?: boolean;
  credential_check_passed?: boolean;
  client_id?: string;
  client_secret?: string;
  domain?: string;
  state?: TFundraiserState;
  _cls?: TFundraiserCRM;
}

const FAKE_ID = '000000000000000000000000';

interface IStripeConnectAccount {
  id: string;
  state: TFundraiserState;
}

export interface IFundraiser extends IBaseAttributes {
  readonly stripe_connect_account: Readonly<IStripeConnectAccount>;
  readonly account: IAccount['id'];
  media_credit_event?: IProject['id'];
  crm?: IFundraiserCRMIntegration;
}

export class Fundraiser extends Model<IFundraiser> {
  public readonly className = 'Fundraiser';
  public override collection: Fundraisers<this> | null = null;

  public get constraints(): TConstraints<IFundraiser> {
    return {
      'crm.ready_to_enable_integration': {
        presence: {
          allowEmpty: false,
          message:
            '^You must confirm that you are ready to enable the integration before connecting',
        },
      },
      'crm._cls': {
        presence: {
          allowEmpty: false,
          message: '^CRM vendor must be chosen',
        },
      },
      'crm.client_id': {
        presence: {
          allowEmpty: false,
          message: '^Client ID cannot be blank',
        },
      },
      'crm.client_secret': {
        presence: {
          allowEmpty: false,
          message: '^Client secret cannot be blank',
        },
      },
      'crm.domain': {
        presence: { message: '^CRM domain name cannot be blank', allowEmpty: false },
      },
      'crm.credential_check_passed': {
        presence: {
          allowEmpty: false,
          message: '^CRM credentials must be verified and they must be valid',
        },
      },
    };
  }

  constructor(attributes: IFundraiser) {
    super(attributes);
  }

  /**
   * Generic PATCH for this fundraiser instance.
   * @param data Partial<IFundraiser> fields to patch
   */
  public async patchIntegration(data: Partial<IFundraiser>): Promise<Fundraiser> {
    return this.withUpdating(() => {
      this.assertCollection(this.collection);
      return this.collection.patchIntegration(data);
    });
  }

  public async checkCRM(data: Partial<IFundraiser>): Promise<Fundraiser> {
    return this.withUpdating(() => {
      this.assertCollection(this.collection);
      return this.collection.checkCRM(data);
    });
  }

  public async disconnectCRM(): Promise<void> {
    return this.withUpdating(() => {
      this.assertCollection(this.collection);
      return this.collection.disconnectCRM(this.id);
    });
  }

  public get mediaCreditEvent(): IProject['id'] | undefined {
    // For dev purposes, fake the ID to simulate undefined.
    const id = this.get('media_credit_event');
    // TODO: Get real ID
    return id === FAKE_ID ? undefined : id;
  }

  /** Replaces model.patch() to use the fundraiser-specific endpoint */
  public async updateMediaCreditProject(): Promise<Fundraiser> {
    const project = this.get('media_credit_event');

    if (!project) {
      throw new Error('Media credit event is required');
    }
    return this.withUpdating(() => {
      this.assertCollection(this.collection);
      return this.collection.updateMediaCreditProject(project);
    });
  }

  public async devClearProject(): Promise<Fundraiser> {
    return this.withUpdating(() => {
      this.assertCollection(this.collection);
      return this.collection.devClearProject();
    });
  }

  @action
  private async withUpdating<T>(op: () => Promise<T>): Promise<T> {
    try {
      this.isUpdating = true;
      return await op();
    } catch (e: unknown) {
      if (e instanceof AggregateError) {
        this.processErrorResponse(e);
      } else {
        this.processErrorResponse(new AggregateError([e]));
      }
      throw e;
    } finally {
      // processJSONResponse resets isUpdating on success; ensure it resets on error as well
      runInAction(() => {
        this.isUpdating = false;
      });
    }
  }
}

export class Fundraisers<Model extends Fundraiser = Fundraiser> extends Collection<Model> {
  public getModel(attributes: Partial<Attributes<Model>>): Model {
    return new Fundraiser(attributes as IFundraiser) as Model;
  }

  public getClassName(): string {
    return 'fundraiser';
  }

  public override url(variant?: string): string {
    if (variant === 'crm') {
      return `${this.getHostname()}billing/fundraiser/crm`;
    }

    if (variant === 'crm-healthcheck') {
      return `${this.getHostname()}billing/fundraiser/crm/healthcheck`;
    }

    /** Standard URLs end with a forward-slash */
    return `${this.getHostname()}billing/fundraiser`;
  }

  public async getOrCreate(): Promise<Fundraiser> {
    const response = await wretch<IFundraiser>(this.url(), {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return this.processJSONResponse(response);
  }

  /**
   * Generic PATCH for fundraiser fields.
   * We use a non-standard endpoint because there is only one fundraiser object per account; hence, we patch /fundraiser and not /fundraiser/:id/.
   * @param data Partial<IFundraiser> fields to patch
   */
  public async patchIntegration(data: Partial<IFundraiser>): Promise<Fundraiser> {
    const response = await wretch<IFundraiser>(this.url(), {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return this.processJSONResponse(response);
  }

  /**
   * Check CRM connection health. This is used to know if the credentials provided are valid.
   * @param data Partial<IFundraiser> fields to check CRM connection
   */
  public async checkCRM(data: Partial<IFundraiser>): Promise<Fundraiser> {
    const response = await wretch<IFundraiser>(this.url('crm-healthcheck'), {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        // Only POST the necessary CRM fields
        crm: {
          _cls: data.crm?._cls,
          client_id: data.crm?.client_id,
          client_secret: data.crm?.client_secret,
          domain: data.crm?.domain,
        },
      }),
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return this.processJSONResponse(response);
  }

  /**
   * Disconnect CRM integration
   */
  @action
  public async disconnectCRM(id: string): Promise<void> {
    const model = this.modelsById.get(id);
    if (model) {
      model.isUpdating = true;
      const response = await wretch<IFundraiser>(this.url('crm'), {
        method: 'DELETE',
        headers: this.getHeaders(),
      });
      if (isWretchError(response)) {
        this.processErrorResponse(model, response.error);
        throw response.error;
      } else {
        runInAction(() => {
          model.isUpdating = false;
          model.isErrored = false;
          model.error = null;
          model.isPending = false;
        });
        this.remove(id);
      }
    }
  }

  public async updateMediaCreditProject(projectId: IProject['id']): Promise<Fundraiser> {
    const response = await wretch<IFundraiser>(this.url(), {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify({ media_credit_event: projectId }),
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return this.processJSONResponse(response);
  }

  public async devClearProject(): Promise<Fundraiser> {
    if (process.env.NODE_ENV === 'production') {
      throw new Error('Dev clear project is not allowed in production');
    }

    const response = await wretch<IFundraiser>(this.url(), {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify({ media_credit_event: FAKE_ID }),
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return this.processJSONResponse(response);
  }
}
